import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaSun, FaMoon } from "react-icons/fa";
import SellerProfileDropdownMenu from "./SellerProfileDropdownMenu";
import { useNotification } from "../../context/NotificationContext";
import { useAuth } from "../../auth/AuthContext";
const SellerNavbar = ({ toggleDarkMode, darkMode }) => {
  const { unreadCount } = useNotification();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { user, isAuthenticated, authReady } = useAuth();
  const hasResolvedUser = isAuthenticated && !!user;
  const isResolvingSessionUser = !authReady;

  // Check if the current user has the 'seller' role
  const isSeller = user && user.roles && user.roles.includes("seller");
  // Profile dropdown state
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  // Ref for profile dropdown
  const profileDropdownRef = React.useRef(null);

  // Close profile dropdown on outside click
  useEffect(() => {
    if (!isProfileDropdownOpen) return;
    function handleClickOutside(event) {
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target)
      ) {
        setIsProfileDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isProfileDropdownOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <nav className="bg-surface shadow-lg sticky top-0 z-50 border-b border-app dark:shadow-none text-app">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link
              to="/"
              className="flex items-center text-gray-800 font-bold text-xl no-underline dark:text-gray-200"
            >
              <img
                src="/logo.png"
                alt="AkkoRent Seller"
                className="w-10 h-10 mr-2 rounded-lg object-cover"
              />
              <span className="text-[#2563EB] dark:text-[#2563EB]">AkkoRent</span>
              {/* <span className="text-[#2563EB] px-1">Seller</span> */}
            </Link>
          </div>

          {!isMenuOpen && (
            <div
              className=" md:hidden cursor-pointer p-2 rounded transition absolute right-16 top-8 transform -translate-y-1/2"
              onClick={toggleDarkMode}
            >
              {darkMode ? (
                <FaSun className="text-yellow-400 text-lg" />
              ) : (
                <FaMoon className="text-gray-800 dark:text-gray-200 text-lg" />
              )}
            </div>
          )}

          {/* Mobile menu toggle */}
          <div
            className="md:hidden flex flex-col cursor-pointer p-1"
            onClick={toggleMenu}
          >
            <span
              className={`w-6 h-0.5 bg-gray-800 dark:bg-gray-200 my-0.5 transition-all duration-300 ${
                isMenuOpen ? "transform rotate-45 translate-y-1.5" : ""
              }`}
            ></span>
            <span
              className={`w-6 h-0.5 bg-gray-800 dark:bg-gray-200 my-0.5 transition-all duration-300 ${
                isMenuOpen ? "opacity-0" : ""
              }`}
            ></span>
            <span
              className={`w-6 h-0.5 bg-gray-800 dark:bg-gray-200 my-0.5 transition-all duration-300 ${
                isMenuOpen ? "transform -rotate-45 -translate-y-1.5" : ""
              }`}
            ></span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center flex-1 justify-between ml-4 lg:ml-8">
            <ul className="flex list-none m-0 p-0 space-x-4 lg:space-x-8">
              {[
                { to: "/", label: "Home" },
                ...(isSeller
                  ? [
                      { to: "/seller", label: "Dashboard" },
                      { to: "/seller/listings", label: "My Listings" },
                      { to: "/seller/plans", label: "Plans" },
                    ]
                  : []),
              ].map(({ to, label, hideBelow }) => {
                // ✅ Match exact page only
                const isActive = location.pathname === to;

                return (
                  <li
                    key={to}
                    className={hideBelow ? `hidden ${hideBelow}:block` : ""}
                  >
                    <Link
                      to={to}
                      className={`relative font-medium py-2 no-underline text-sm lg:text-base transition-colors duration-300 group ${
                        isActive
                          ? "text-[#2563EB] font-semibold"
                          : "text-gray-600 dark:text-gray-200 hover:text-[#2563EB] dark:hover:text-[#2563EB]"
                      }`}
                    >
                      {label}
                      <span
                        className={`absolute bottom-0 left-0 h-0.5 bg-[#2563EB] transition-all duration-300 ${
                          isActive ? "w-full" : "w-0 group-hover:w-full"
                        }`}
                      ></span>
                    </Link>
                  </li>
                );
              })}
            </ul>

            {/* Notifications & Profile */}
            <div className="flex items-center space-x-2 lg:space-x-4">
              <div className="flex items-center space-x-2 lg:space-x-4 mx-4 lg:mx-8">
                <Link
                  to="/seller/listings/add"
                  className="flex items-center text-white bg-[#2563EB] py-2 lg:py-2.5 px-4 lg:px-6 rounded-lg font-medium hover:bg-blue-700 transition-all duration-300 space-x-1 lg:space-x-2 no-underline shadow-md hover:shadow-lg transform hover:scale-105 text-sm lg:text-base dark:shadow-none dark:hover:shadow-none"
                >
                  <svg
                    width="16"
                    height="16"
                    className="lg:w-[18px] lg:h-[18px]"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  <span className="hidden lg:inline">Add Listing</span>
                  <span className="lg:hidden">Add</span>
                </Link>
              </div>

              <Link
                to="/seller/notifications"
                className={`
    relative p-2 rounded-full transition-all duration-300 no-underline
    ${
      location.pathname === "/seller/notifications"
        ? "text-[#2563EB] bg-blue-50 dark:bg-gray-800"
        : "text-gray-600 dark:text-gray-200 hover:text-[#2563EB] dark:hover:text-[#2563EB] hover:bg-gray-50 dark:hover:bg-gray-700"
    }
  `}
              >
                <svg
                  width="20"
                  height="20"
                  className="lg:w-6 lg:h-6"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                </svg>

                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 lg:w-5 lg:h-5 flex items-center justify-center text-xs font-bold dark:text-gray-200">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </Link>

              {/* Profile Dropdown */}
              {hasResolvedUser ? (
                <div className="relative" ref={profileDropdownRef}>
                  <button
                    onClick={() => setIsProfileDropdownOpen((v) => !v)}
                    className="flex items-center space-x-2 lg:space-x-3 p-1 lg:p-2 text-gray-600 hover:text-[#2563EB] hover:bg-gray-50 rounded-lg transition-all duration-300 dark:text-gray-200 dark:hover:text-[#2563EB] dark:hover:bg-gray-700"
                  >
                    <div className="w-7 h-7 lg:w-8 lg:h-8 bg-[#2563EB] rounded-full flex items-center justify-center">
                      {/* Avatar SVG Icon or user avatar */}
                      {user.avatar ? (
                        <img
                          src={user.avatar}
                          alt={user.name || user.first_name || "User"}
                          className="w-6 h-6 lg:w-7 lg:h-7 rounded-full object-cover"
                        />
                      ) : (
                        <svg
                          className="w-6 h-6 lg:w-7 lg:h-7 text-white"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <circle cx="12" cy="8" r="4" />
                          <path d="M4 20c0-4 4-7 8-7s8 3 8 7" />
                        </svg>
                      )}
                    </div>
                    <div className="hidden xl:block text-left">
                      <p className="text-sm font-medium dark:text-gray-200">
                        {(user.first_name || user.firstName) ? `${user.first_name || user.firstName} ${user.last_name || user.lastName || ""}`.trim() : user.name || user.username || "User"}
                      </p>
                    </div>
                    <svg
                      width="14"
                      height="14"
                      className={`lg:w-4 lg:h-4 transition-transform duration-300 ${
                        isProfileDropdownOpen ? "rotate-180" : ""
                      }`}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="6,9 12,15 18,9"></polyline>
                    </svg>
                  </button>

                  {/* Profile Dropdown Menu */}
                  {isProfileDropdownOpen && (
                    <SellerProfileDropdownMenu
                      user={user}
                      toggleDarkMode={toggleDarkMode}
                      darkMode={darkMode}
                      onClose={() => setIsProfileDropdownOpen(false)}
                    />
                  )}
                </div>
              ) : isResolvingSessionUser ? (
                <div className="flex items-center text-gray-500 bg-gray-100 py-2 px-4 rounded-lg font-medium dark:bg-gray-700 dark:text-gray-300">
                  Account
                </div>
              ) : (
                <Link
                  to="/login"
                  className="flex items-center text-white bg-[#2563EB] py-2 px-4 rounded-lg font-medium hover:bg-[#2563EB] transition-colors duration-300 space-x-2 no-underline dark:bg-[#2563EB] dark:hover:bg-[#2563EB]"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  <span>Login</span>
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <div
          className={`md:hidden transition-all duration-300 ${
            isMenuOpen
              ? "max-h-[90vh] overflow-y-auto opacity-100"
              : "max-h-0 opacity-0 overflow-hidden"
          }`}
        >
          <div className="py-4 space-y-2">
            <Link
              to="/"
              className="flex items-center py-3 text-gray-600 hover:text-[#2563EB] border-b border-gray-200 no-underline space-x-3 dark:text-gray-200 dark:hover:text-[#2563EB]"
              onClick={() => setIsMenuOpen(false)}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                ></path>
              </svg>
              <span>Home</span>
            </Link>

            {isSeller && (
              <Link
                to="/seller"
                className="flex items-center py-3 text-gray-600 hover:text-[#2563EB] border-b border-gray-200 no-underline space-x-3 dark:text-gray-200 dark:hover:text-[#2563EB]"
                onClick={() => setIsMenuOpen(false)}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"
                  ></path>
                </svg>
                <span>Dashboard</span>
              </Link>
            )}

            {isSeller && (
              <Link
                to="/seller/listings"
                className="flex items-center py-3 text-gray-600 hover:text-[#2563EB] border-b border-gray-200 no-underline space-x-3 dark:text-gray-200 dark:hover:text-[#2563EB]"
                onClick={() => setIsMenuOpen(false)}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  ></path>
                </svg>
                <span>My Listings</span>
              </Link>
            )}

            {isSeller && (
              <Link
                to="/seller/plans"
                className="flex items-center py-3 text-gray-600 hover:text-[#2563EB] border-b border-gray-200 no-underline space-x-3 dark:text-gray-200 dark:hover:text-[#2563EB]"
                onClick={() => setIsMenuOpen(false)}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                  ></path>
                </svg>
                <span>Plans</span>
              </Link>
            )}

            {/* Messages */}
            <Link
              to="/messages"
              className="flex items-center gap-3 py-3 text-gray-600 hover:text-[#2563EB] border-b border-gray-200 no-underline dark:text-gray-200 dark:border-gray-700"
              onClick={() => setIsMenuOpen(false)}
            >
              <svg
                className="w-5 h-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
              <span>Messages</span>
            </Link>

            {/* Settings */}
            <Link
              to="/account"
              className="flex items-center gap-3 py-3 text-gray-600 hover:text-[#2563EB] border-b border-gray-200 no-underline dark:text-gray-200 dark:border-gray-700"
              onClick={() => setIsMenuOpen(false)}
            >
              <svg
                className="w-5 h-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06 a1.65 1.65 0 0 0 .33-1.82  1.65 1.65 0 0 0-1.51-1H3 a2 2 0 1 1 0-4h.09 a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06  a2 2 0 1 1 2.83-2.83l.06.06  a1.65 1.65 0 0 0 1.82.33h0 a1.65 1.65 0 0 0 1-1.51V3  a2 2 0 1 1 4 0v.09  a1.65 1.65 0 0 0 1 1.51  1.65 1.65 0 0 0 1.82-.33l.06-.06  a2 2 0 1 1 2.83 2.83l-.06.06 a1.65 1.65 0 0 0-.33 1.82v0  a1.65 1.65 0 0 0 1.51 1H21  a2 2 0 1 1 0 4h-.09 a1.65 1.65 0 0 0-1.51 1z"></path>
              </svg>
              <span>Account</span>
            </Link>

            {/* Help & Support */}
            <Link
              to="/support"
              className="flex items-center gap-3 py-3 text-gray-600 hover:text-[#2563EB] border-b border-gray-200 no-underline dark:text-gray-200 dark:border-gray-700"
              onClick={() => setIsMenuOpen(false)}
            >
              <svg
                className="w-5 h-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
              <span>Help & Support</span>
            </Link>

            {/* Customer View */}
            <Link
              to="/"
              className="flex items-center gap-3 py-3 text-green-600 hover:text-green-700 border-b border-gray-200 no-underline dark:text-green-400 dark:border-gray-700"
              onClick={() => setIsMenuOpen(false)}
            >
              <svg
                className="w-5 h-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
              <span>Customer View</span>
            </Link>

            {/* Mobile Quick Actions */}
            <div className="pt-4 space-y-3">
              <Link
                to="/seller/listings/add"
                className="flex items-center justify-center text-white bg-[#2563EB] py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-all duration-300 space-x-2 no-underline dark:shadow-none dark:hover:shadow-none"
                onClick={() => setIsMenuOpen(false)}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                <span>Add New Listing</span>
              </Link>

              <Link
                to="/seller/notifications"
                className="flex items-center justify-center text-gray-600 py-3 px-4 rounded-lg hover:text-[#2563EB] hover:bg-gray-50 transition-all duration-300 relative space-x-2 no-underline dark:text-gray-200 dark:hover:text-[#2563EB] dark:hover:bg-gray-700"
                onClick={() => setIsMenuOpen(false)}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                </svg>
                <span className="font-medium">Notifications</span>
                {unreadCount > 0 && (
                  <span className="relative -top-2 -right-0 -ml-4 bg-red-500 text-white rounded-full w-4 h-4 lg:w-5 lg:h-5 flex items-center justify-center text-xs font-bold dark:text-gray-200">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </Link>
            </div>

            {/* Mobile Profile Section */}
            {hasResolvedUser ? (
              <Link
                to="/profile"
                className="flex items-center justify-center text-white bg-green-500 py-2 px-4 rounded-lg font-medium hover:bg-green-600 transition-colors duration-300 space-x-2 no-underline dark:bg-green-400 dark:hover:bg-green-500"
                onClick={() => setIsMenuOpen(false)}
              >
                <div className="w-7 h-7 lg:w-8 lg:h-8 bg-[#2563EB] rounded-full flex items-center justify-center">
                  {user.avatar ? (
                    <img
                      src={user.avatar}
                      alt={user.name || user.first_name || "User"}
                      className="w-6 h-6 lg:w-7 lg:h-7 rounded-full object-cover"
                    />
                  ) : (
                    <svg
                      className="w-6 h-6 lg:w-7 lg:h-7 text-white"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <circle cx="12" cy="8" r="4" />
                      <path d="M4 20c0-4 4-7 8-7s8 3 8 7" />
                    </svg>
                  )}
                </div>
                <span>
                  {(user.first_name || user.firstName) ? `${user.first_name || user.firstName} ${user.last_name || user.lastName || ""}`.trim() : user.name || user.username || "Profile"}
                </span>
              </Link>
            ) : isResolvingSessionUser ? (
              <div className="flex items-center justify-center text-gray-500 bg-gray-100 py-3 px-6 rounded-lg font-medium dark:bg-gray-700 dark:text-gray-300">
                Account
              </div>
            ) : (
              <Link
                to="/login"
                className="flex items-center justify-center text-white bg-[#2563EB] py-3 px-6 rounded-lg font-medium hover:bg-[#2563EB] transition-colors duration-300 space-x-2 no-underline dark:bg-[#2563EB] dark:hover:bg-[#2563EB]"
                onClick={() => setIsMenuOpen(false)}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                <span>Login</span>
              </Link>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default SellerNavbar;
